import { Users, Wrench, FileText, DollarSign, Calendar, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import ExpressJobWorkflow from './ExpressJobWorkflow';
import QuickActions from './QuickActions';
import { useTheme } from './ThemeContext';
import { formatDisplayDateLong } from './dateUtils';

export default function HomeDashboard({ userProfile, onNavigate, companyId }) {
  const { colors } = useTheme();
  const [showExpressWorkflow, setShowExpressWorkflow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayJobs: 0,
    completedToday: 0,
    activeJobs: 0,
    upcomingJobs: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    pendingQuotes: 0,
    totalCustomers: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);

  useEffect(() => {
    if (companyId) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      // Fetch all data in parallel
      const [jobsData, quotesData, customersData, invoicesData] = await Promise.all([
        supabaseData.from('jobs').select('*'),
        supabaseData.from('quotes').select('*'),
        supabaseData.from('customers').select('id'),
        supabaseData.from('invoices').select('total, created_at')
      ]);
      
      const jobs = jobsData.data || [];
      const quotes = quotesData.data || [];
      const customers = customersData.data || [];
      const invoices = invoicesData.data || [];
      
      // Calculate stats
      const todayJobs = jobs.filter(j => j.scheduled_date === today);
      const completedToday = todayJobs.filter(j => j.status === 'completed');
      const activeJobs = jobs.filter(j => j.status === 'in-progress');
      const upcomingJobs = jobs.filter(j => j.status === 'scheduled' && j.scheduled_date > today);
      const pendingQuotes = quotes.filter(q => q.status === 'draft' || q.status === 'sent');
      
      const weekRevenue = invoices
        .filter(inv => inv.created_at >= weekAgo)
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      
      const monthRevenue = invoices
        .filter(inv => inv.created_at >= monthAgo)
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      
      setStats({
        todayJobs: todayJobs.length,
        completedToday: completedToday.length,
        activeJobs: activeJobs.length,
        upcomingJobs: upcomingJobs.length,
        weekRevenue: weekRevenue,
        monthRevenue: monthRevenue,
        pendingQuotes: pendingQuotes.length,
        totalCustomers: customers.length
      });
      
      // Get recent jobs with customer info
      const { data: recentJobsData } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            first_name,
            last_name,
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);
      
      setRecentJobs(recentJobsData || []);
      
      // Get recent quotes
      const { data: recentQuotesData } = await supabaseData
        .from('quotes')
        .select(`
          *,
          customers (
            first_name,
            last_name,
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);
      
      setRecentQuotes(recentQuotesData || []);
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (module) => {
    if (module === 'express-job') {
      setShowExpressWorkflow(true);
    } else if (onNavigate) {
      onNavigate(module);
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown Customer';
    if (customer.first_name && customer.last_name) {
      return `${customer.last_name}, ${customer.first_name}`;
    }
    if (customer.last_name) return customer.last_name;
    if (customer.first_name) return customer.first_name;
    if (customer.name) return customer.name;
    return 'Unknown Customer';
  };

  if (showExpressWorkflow) {
    return (
      <ExpressJobWorkflow
        companyId={companyId}
        onClose={() => {
          setShowExpressWorkflow(false);
          loadDashboardData(); // Reload data when workflow closes
        }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: colors.textMuted
      }}>
        Loading dashboard...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Quick Actions - NOW AT TOP */}
      <QuickActions onNavigate={handleNavigate} companyId={companyId} />

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
        gap: '2.5rem',
        marginBottom: '2rem'
      }}>
        {/* Today's Jobs */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.accent }}>
                {stats.todayJobs}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Today's Jobs</div>
            </div>
            <Calendar size={32} color={colors.accent} style={{ opacity: 0.5 }} />
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.success }}>
            ✓ {stats.completedToday} completed
          </div>
        </div>

        {/* Active Jobs */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.info }}>
                {stats.activeJobs}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Active Jobs</div>
            </div>
            <Wrench size={32} color={colors.info} style={{ opacity: 0.5 }} />
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
            {stats.upcomingJobs} upcoming
          </div>
        </div>

        {/* Weekly Revenue */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.success }}>
                ${stats.weekRevenue.toLocaleString()}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>This Week</div>
            </div>
            <DollarSign size={32} color={colors.success} style={{ opacity: 0.5 }} />
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
            ${stats.monthRevenue.toLocaleString()} this month
          </div>
        </div>

        {/* Pending Quotes */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.warning }}>
                {stats.pendingQuotes}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Pending Quotes</div>
            </div>
            <FileText size={32} color={colors.warning} style={{ opacity: 0.5 }} />
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
            {stats.totalCustomers} total customers
          </div>
        </div>
      </div>

      {/* Recent Activity Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
        gap: '1.5rem'
      }}>
        {/* Recent Jobs */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: colors.text, margin: 0 }}>
              Recent Jobs
            </h3>
            <button
              onClick={() => onNavigate && onNavigate('jobs')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                cursor: 'pointer',
                fontSize: '0.9rem',
                textDecoration: 'underline'
              }}
            >
              View All →
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {recentJobs.length === 0 ? (
              <div style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>
                No jobs yet
              </div>
            ) : (
              recentJobs.map(job => (
                <div
                  key={job.id}
                  style={{
                    background: colors.bgInput,
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                        {getCustomerName(job.customers)}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                        {job.job_number || 'No job number'}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      background: job.status === 'completed' ? '#1a4d2e' : job.status === 'in-progress' ? '#2a3a4d' : '#4d3a1a',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'capitalize'
                    }}>
                      {job.status?.replace('-', ' ')}
                    </div>
                  </div>
                  {job.scheduled_date && (
                    <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                      📅 {formatDisplayDateLong(job.scheduled_date)}
                      {job.scheduled_time && ` at ${job.scheduled_time.substring(0, 5)}`}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Quotes */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: colors.text, margin: 0 }}>
              Recent Quotes
            </h3>
            <button
              onClick={() => onNavigate && onNavigate('quotes')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                cursor: 'pointer',
                fontSize: '0.9rem',
                textDecoration: 'underline'
              }}
            >
              View All →
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {recentQuotes.length === 0 ? (
              <div style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>
                No quotes yet
              </div>
            ) : (
              recentQuotes.map(quote => (
                <div
                  key={quote.id}
                  style={{
                    background: colors.bgInput,
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                        {getCustomerName(quote.customers)}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                        {quote.quote_number || 'No quote number'}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      background: quote.status === 'signed' ? '#1a4d2e' : quote.status === 'sent' ? '#2a3a4d' : '#4d3a1a',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'capitalize'
                    }}>
                      {quote.status}
                    </div>
                  </div>
                  {quote.total && (
                    <div style={{ fontSize: '0.9rem', color: colors.success, marginTop: '0.5rem', fontWeight: 'bold' }}>
                      ${quote.total.toLocaleString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
